// src/pages/ViewStudentPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, Grid, Divider, Stack, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Avatar, CircularProgress,
  Chip, Tooltip
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { motion } from 'framer-motion';

import { useParams } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import BookIcon from '@mui/icons-material/Book';
import PrintIcon from '@mui/icons-material/Print';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import NoteIcon from '@mui/icons-material/Note';
import { useNavigate } from 'react-router-dom';
import { fetchSchoolYears } from '../slices/schoolYearsSlice';
import PaymentsIcon from '@mui/icons-material/Payments';
import HistoryIcon from '@mui/icons-material/History';
import { fetchStudentById, closeStudentAccount } from '../slices/studentsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBuses } from '../slices/busesSlice';
import { useToast } from '../context/ToastContext';
import { exportStudentDetailsToPrint } from '../utils/exportUtils';
import { isTeacher, TEACHER_PERMISSIONS } from '../config/teacherPermissions';
import { fetchBorrowTransactions } from '../slices/librarySlice';
import StudentHealthSection from '../components/health/StudentHealthSection';

// ── helpers ──────────────────────────────────────────────────────────────────

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: 'easeOut', delay },
});

const SECTION_RADIUS = 3;

/** A labelled info row with a leading icon */
const InfoRow = ({ icon, label, value, color }) => (
  <Stack useFlexGap direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 0.75 }}>
    <Box
      sx={{
        color: color || '#4338CA',
        bgcolor: color ? `${color}14` : '#EEF2FF',
        width: 34, height: 34, borderRadius: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        variant="caption"
        display="block"
        lineHeight={1.3}
        sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
      >
        {value || 'غير محدد'}
      </Typography>
    </Box>
  </Stack>
);

/** A coloured stat card */
const StatCard = ({ label, value, color, bg }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 3,
      background: bg || '#EEF2FF',
      border: `1px solid ${color || '#4338CA'}22`,
      textAlign: 'center',
      flex: 1,
      minWidth: 120,
    }}
  >
    <Typography
      variant="caption"
      fontWeight={700}
      display="block"
      sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
    >
      {label}
    </Typography>
    <Typography
      variant="h6"
      fontWeight={800}
      sx={{ color: color || '#4338CA', mt: 0.5, fontFamily: 'Cairo, Tajawal, sans-serif' }}
    >
      {value}
    </Typography>
  </Paper>
);

/** Styled section header */
const SectionHeader = ({ icon, title, color = '#4338CA' }) => (
  <Stack useFlexGap direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 38, height: 38, borderRadius: 2.5,
        background: `${color}14`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="subtitle1"
      fontWeight={700}
      sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
    >
      {title}
    </Typography>
  </Stack>
);

/** Styled table header cell */
const TH = ({ children }) => (
  <TableCell
    sx={{
      fontWeight: 700,
      background: '#EEF2FF',
      color: '#4338CA',
      borderBottom: '2px solid #E5E7EB',
      fontSize: '0.85rem',
      fontFamily: 'Cairo, Tajawal, sans-serif',
    }}
  >
    {children}
  </TableCell>
);

/** Styled table body row */
const TBodyRow = ({ children, idx }) => (
  <TableRow
    sx={{
      background: idx % 2 === 0 ? '#F8F9FF' : '#fff',
      '&:hover': { background: '#EEF2FF' },
      transition: 'background 0.18s',
      '& .MuiTableCell-root': { fontFamily: 'Cairo, Tajawal, sans-serif', color: '#1E1B4B' },
    }}
  >
    {children}
  </TableRow>
);

// ── Component ─────────────────────────────────────────────────────────────────

const ViewStudentPage = () => {
  const { studentId } = useParams();
  const dispatch = useDispatch();
  const isTeacherUser = isTeacher();
  const hideFinancial = isTeacherUser && TEACHER_PERMISSIONS.students.hideFinancialActions;
  const { list: schoolYears, loading } = useSelector((state) => state.schoolYears);
  const { selectedStudent: student, selectedStudentStatus } = useSelector(state => state.students);
  const activeYear = schoolYears.find(y => y.is_active);
  const [selectedYear, setSelectedYear] = useState(activeYear || null);
  const hasSetInitialYear = useRef(!!activeYear);
  // schoolYears loads asynchronously, so activeYear is usually still undefined on first
  // render; sync selectedYear to it once it becomes available (only the first time, so a
  // manual "all years" clear by the user afterward isn't overridden).
  useEffect(() => {
    if (!hasSetInitialYear.current && activeYear) {
      setSelectedYear(activeYear);
      hasSetInitialYear.current = true;
    }
  }, [activeYear]);
  const payments = (student?.recipients || []).filter(p =>
    selectedYear ? p.school_year === selectedYear.id : true
  );
  const { showToast } = useToast();
  const [totalDue, setTotalDue] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [feeInputs, setFeeInputs] = useState();
  const [previousDebt, setPreviousDebt] = useState(0);

  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);

  const [libraryTransactions, setLibraryTransactions] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryDataFetched, setLibraryDataFetched] = useState(false);

  useEffect(() => {
    dispatch(fetchBuses());
    dispatch(fetchStudentById(studentId));
    dispatch(fetchSchoolYears());
  }, [studentId, dispatch]);

  useEffect(() => {
    const fetchLibraryData = async () => {
      if (!student?.id || libraryDataFetched) return;
      setLibraryLoading(true);
      setLibraryDataFetched(true);
      try {
        const transactions = await dispatch(fetchBorrowTransactions({ member__student: student.id })).unwrap();
        setLibraryTransactions(Array.isArray(transactions) ? transactions : []);
      } catch {
        setLibraryTransactions([]);
      } finally {
        setLibraryLoading(false);
      }
    };
    if (student?.id && !libraryDataFetched) fetchLibraryData();
  }, [student?.id, dispatch, libraryDataFetched]);

  useEffect(() => {
    setLibraryDataFetched(false);
    setLibraryTransactions([]);
  }, [studentId]);

  useEffect(() => {
    // Prefer new fee_categories system; fall back to old school_fees fixed fields
    if (student?.fee_categories?.total_after_discount !== undefined) {
      setTotalDue(parseFloat(student.fee_categories.total_after_discount || 0));
      setPreviousDebt(parseFloat(student.fee_categories.previous_debt || 0));
      setFeeInputs(null);
    } else {
      const feeSource = Array.isArray(student?.school_fees_by_year)
        ? student.school_fees_by_year.find(fee => fee.school_year === selectedYear?.id)
        : student?.school_fees;
      if (feeSource) {
        const annualFee = parseFloat(feeSource.school_fee || 0);
        const booksFee = parseFloat(feeSource.books_fee || 0);
        const transportFee = parseFloat(feeSource.trans_fee || 0);
        const uniformFee = parseFloat(feeSource.clothes_fee || 0);
        const debt = parseFloat(feeSource.previous_debt || 0);
        setFeeInputs({ annualFee, booksFee, transportFee, uniformFee });
        setPreviousDebt(debt);
        setTotalDue(annualFee + booksFee + transportFee + uniformFee + debt);
      } else {
        setFeeInputs(null);
        setPreviousDebt(0);
        setTotalDue(0);
      }
    }
  }, [student, selectedYear]);

  useEffect(() => {
    setRemaining(totalDue - paidAmount);
  }, [totalDue, paidAmount]);

  const [yearToClose, setYearToClose] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [totalReceived, setTotalReceived] = useState(0);
  const [totalUnreceived, setTotalUnreceived] = useState(0);

  useEffect(() => {
    const total = payments.reduce((acc, p) => acc + parseFloat(p?.amount || 0), 0);
    setPaidAmount(total);
  }, [payments]);

  useEffect(() => {
    if (student?.fee_categories?.total_after_discount !== undefined) {
      setTotalDue(parseFloat(student.fee_categories.total_after_discount || 0));
      setPreviousDebt(parseFloat(student.fee_categories.previous_debt || 0));
      setFeeInputs(null);
    } else {
      const feeSource = student?.school_fees;
      if (feeSource) {
        const annualFee = parseFloat(feeSource.school_fee || 0);
        const booksFee = parseFloat(feeSource.books_fee || 0);
        const transportFee = parseFloat(feeSource.trans_fee || 0);
        const uniformFee = parseFloat(feeSource.clothes_fee || 0);
        const debt = parseFloat(feeSource.previous_debt || 0);
        setFeeInputs({ annualFee, booksFee, transportFee, uniformFee });
        setPreviousDebt(debt);
        setTotalDue(annualFee + booksFee + transportFee + uniformFee + debt);
      }
    }
  }, [student]);

  useEffect(() => {
    if (payments && payments.length > 0) {
      const total = payments.reduce((acc, p) => acc + parseFloat(p?.amount || 0), 0);
      setPaidAmount(total);
      const received = payments
        .filter(p => p.received === true)
        .reduce((acc, p) => acc + parseFloat(p?.amount || 0), 0);
      setTotalReceived(received);
      setTotalUnreceived(total - received);
    } else {
      setPaidAmount(0);
      setTotalReceived(0);
      setTotalUnreceived(0);
    }
  }, [payments]);

  const classes = useSelector(state => state.classes?.list || []);
  const navigate = useNavigate();
  const { list: busList } = useSelector((state) => state.buses);
  const history = student?.history || [];
  const [open, setOpen] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', date: '' });

  if (!student) {
    if (selectedStudentStatus === 'failed') {
      return <Typography align="center" color="error">الطالب غير موجود</Typography>;
    }
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const handleViewDocument = () => setDocumentViewerOpen(true);

  const handleDownloadDocument = () => {
    if (student.attachment_url || student.attachment) {
      const url = student.attachment_url || student.attachment;
      const link = document.createElement('a');
      link.href = url;
      link.download = `${student.first_name}_${student.second_name}_document`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const isImageFile = (url) => {
    if (!url) return false;
    return ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'].some(ext => url.toLowerCase().includes(ext));
  };

  const isPdfFile = (url) => url ? url.toLowerCase().includes('.pdf') : false;

  const handleOpen = () => navigate('/students/payment', { state: { student } });

  const handleCloseSchooleFee = () => setOpenDialog(true);

  const handleConfirmClose = async () => {
    await dispatch(closeStudentAccount({ studentId: student.id, year: yearToClose })).unwrap();
    showToast('تم تسكير الحساب بنجاح', 'success');
    setOpenDialog(false);
  };

  const handleClose = () => {
    setOpen(false);
    setNewPayment({ amount: '', date: '' });
  };

  const handleAddPayment = () => {
    if (newPayment.amount && newPayment.date) {
      payments.push({ amount: parseFloat(newPayment.amount), date: newPayment.date });
      handleClose();
    }
  };

  const handlePrint = () => {
    let busInfo = null;
    if (student.is_bus_joined && busList && Array.isArray(busList)) {
      const bus = busList.find(b => b.id === student.bus);
      if (bus) busInfo = { plateNumber: bus.plateNumber || '', name: bus.name || '' };
    }
    const className = classes.find(c => c.id === student.school_class)?.name || 'غير معروف';
    const paymentsArr = (student?.recipients || []).filter(p =>
      selectedYear ? p.school_year === selectedYear.id : true
    );
    exportStudentDetailsToPrint(student, {
      className, totalDue, paidAmount, totalReceived, totalUnreceived, remaining,
      payments: paymentsArr, busInfo,
    });
  };

  const studentName = `${student.first_name || ''} ${student.second_name || ''}`.trim();
  const initials = [student.first_name?.[0], student.second_name?.[0]].filter(Boolean).join('');
  const busInfo = busList?.find(b => b.id === student.bus);

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          maxWidth: 1400,
          mx: 'auto',
          pb: 4,
        }}
      >
        {/* ── Hero Header ───────────────────────────────────────────────── */}
        <motion.div {...fadeUp(0)}>
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* subtle decorative circle */}
            <Box
              sx={{
                position: 'absolute', right: -60, top: -60,
                width: 240, height: 240,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                pointerEvents: 'none',
              }}
            />
            <Stack useFlexGap
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
            >
              {/* Left: avatar + name */}
              <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 64, height: 64,
                    fontSize: '1.6rem',
                    fontWeight: 800,
                    background: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    border: '2px solid rgba(255,255,255,0.35)',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  {initials || <PersonIcon fontSize="large" />}
                </Avatar>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{ color: '#fff', lineHeight: 1.2, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    {studentName || 'بيانات الطالب'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.3, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    {student?.school_class_name || classes.find(c => c.id === student.school_class)?.name || ''}
                  </Typography>
                  {student.is_archived && (
                    <Chip
                      label="غير نشط"
                      size="small"
                      sx={{ mt: 0.5, background: '#fff', color: '#DC2626', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                    />
                  )}
                </Box>
              </Stack>

              {/* Right: action buttons */}
              <Stack useFlexGap direction="row" spacing={1.2} flexWrap="wrap">
                <Tooltip title="طباعة">
                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    size="small"
                    sx={headerBtnSx}
                  >
                    طباعة
                  </Button>
                </Tooltip>
                {/* HIDDEN TEMPORARILY - عرض الملف الاكاديمي بملف الطالب */}
                {/* <Button
                  variant="contained"
                  startIcon={<BookIcon />}
                  onClick={() => navigate(`/student/${studentId}/academic`)}
                  size="small"
                  sx={headerBtnSx}
                >
                  الملف الأكاديمي
                </Button> */}
                {!isTeacherUser && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/students/edit/${studentId}`)}
                    size="small"
                    sx={headerBtnSolidSx}
                  >
                    تعديل
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </motion.div>

        {/* ── Student Info ──────────────────────────────────────────────── */}
        <motion.div {...fadeUp(0.07)}>
          <SectionCard>
            <SectionHeader icon={<PersonIcon fontSize="small" />} title="تفاصيل الطالب" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<PersonIcon fontSize="small" />} label="الاسم الكامل" value={studentName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<SchoolIcon fontSize="small" />} label="الصف" value={student?.school_class_name || classes.find(c => c.id === student.school_class)?.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<BadgeIcon fontSize="small" />} label="رقم الهوية" value={student.student_id} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label="تاريخ الميلاد" value={student.birthdate} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label="تاريخ التسجيل" value={student.date_of_registration} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<LocationOnIcon fontSize="small" />} label="العنوان" value={student.address || 'فلسطين'} />
              </Grid>
              {student.note && (
                <Grid item xs={12}>
                  <InfoRow icon={<NoteIcon fontSize="small" />} label="ملاحظات" value={student.note} />
                </Grid>
              )}
            </Grid>
          </SectionCard>
        </motion.div>

        {/* ── Guardian Info ─────────────────────────────────────────────── */}
        <motion.div {...fadeUp(0.12)}>
          <SectionCard>
            <SectionHeader icon={<PersonIcon fontSize="small" />} title="معلومات ولي الأمر" color="#0284C7" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<PersonIcon fontSize="small" />} label="الاسم" value={student.parent_name} color="#0284C7" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<PhoneIcon fontSize="small" />} label="هاتف 1" value={`${student.parent_phone || ''} ${student.parent_phone_code || ''}`.trim()} color="#0284C7" />
              </Grid>
              {student.parent_phone_2 && (
                <Grid item xs={12} sm={6}>
                  <InfoRow icon={<PhoneIcon fontSize="small" />} label="هاتف 2" value={`${student.parent_phone_2} ${student.parent_phone_2_code || ''}`.trim()} color="#0284C7" />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <InfoRow icon={<EmailIcon fontSize="small" />} label="البريد الإلكتروني" value={student.parent_email} color="#0284C7" />
              </Grid>
            </Grid>
          </SectionCard>
        </motion.div>

        {/* ── Attachment ────────────────────────────────────────────────── */}
        {(student.attachment_url || student.attachment) && (
          <motion.div {...fadeUp(0.15)}>
            <SectionCard>
              <SectionHeader icon={<AttachFileIcon fontSize="small" />} title="المرفقات والوثائق" color="#5B21F4" />
              <Stack direction="row" alignItems="center" justifyContent="space-between"
                sx={{
                  p: 2, borderRadius: 2.5,
                  background: 'rgba(91,33,244,0.05)',
                  border: '1px solid rgba(91,33,244,0.14)',
                }}
              >
                <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
                  <AttachFileIcon sx={{ color: '#5B21F4' }} />
                  <Box>
                    <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>وثيقة مرفقة للطالب</Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      {isImageFile(student.attachment_url || student.attachment) ? 'صورة' :
                        isPdfFile(student.attachment_url || student.attachment) ? 'ملف PDF' : 'مستند'}
                    </Typography>
                  </Box>
                </Stack>
                <Stack useFlexGap direction="row" spacing={0.5}>
                  <Tooltip title="عرض">
                    <IconButton sx={{ color: '#4338CA' }} onClick={handleViewDocument}><VisibilityIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="تحميل">
                    <IconButton sx={{ color: '#5B21F4' }} onClick={handleDownloadDocument}><DownloadIcon /></IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </SectionCard>
          </motion.div>
        )}

        {/* ── Financial ─────────────────────────────────────────────────── */}
        {!hideFinancial && (
          <>
            <motion.div {...fadeUp(0.18)}>
              <SectionCard>
                <SectionHeader icon={<PaymentsIcon fontSize="small" />} title="ملخص الرسوم" color="#16A34A" />
                <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 2.5 }}>
                  <StatCard label="إجمالي الرسوم" value={`${totalDue.toFixed(2)} ₪`} color="#4338CA" bg="#EEF2FF" />
                  <StatCard label="المجدول" value={`${paidAmount.toFixed(2)} ₪`} color="#16A34A" bg="#DCFCE7" />
                  <StatCard label="المحصّل" value={`${totalReceived.toFixed(2)} ₪`} color="#0284C7" bg="#E0F2FE" />
                  <StatCard
                    label="المتبقي"
                    value={`${(totalDue - totalReceived).toFixed(2)} ₪`}
                    color={(totalDue - totalReceived) > 0 ? '#DC2626' : '#16A34A'}
                    bg={(totalDue - totalReceived) > 0 ? '#FEE2E2' : '#DCFCE7'}
                  />
                </Stack>
                {previousDebt > 0 && (
                  <Chip
                    label={`دين سابق: ${previousDebt.toFixed(2)} ₪`}
                    color="warning"
                    size="small"
                    sx={{ mb: 2, fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  />
                )}
                <Button
                  variant="contained"
                  startIcon={<PaymentsIcon />}
                  onClick={handleOpen}
                  sx={{
                    borderRadius: 2.5, fontWeight: 700, textTransform: 'none',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    bgcolor: '#4338CA',
                    boxShadow: 'none',
                    px: 3, py: 1.1,
                    '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  }}
                >
                  الملف المالي
                </Button>
              </SectionCard>
            </motion.div>

            {/* Payment history */}
            <motion.div {...fadeUp(0.21)}>
              <SectionCard>
                <SectionHeader icon={<HistoryIcon fontSize="small" />} title="سجل دفعات الطالب" color="#0284C7" />
                <Box sx={{ mb: 2, maxWidth: 360 }}>
                  <Autocomplete
                    options={schoolYears}
                    getOptionLabel={(option) => option.label}
                    value={selectedYear}
                    onChange={(e, value) => setSelectedYear(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="السنة الدراسية"
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    fullWidth
                  />
                </Box>
                <TableContainer sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.07)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TH>المبلغ</TH>
                        <TH>التاريخ</TH>
                        <TH>الحالة</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.length > 0 ? payments.map((p, idx) => (
                        <TBodyRow key={idx} idx={idx}>
                          <TableCell sx={{ fontWeight: 600 }}>{p.amount} ₪</TableCell>
                          <TableCell>{p.date}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {p.received
                                ? <Chip label="تم التحصيل" color="success" size="small" variant="outlined" />
                                : <Chip label="لم يتم التحصيل" color="warning" size="small" variant="outlined" />
                              }
                              {p.transferred_from_year && p.transferred_from_year !== p.school_year && (
                                <Chip
                                  label={`مرحّل من ${schoolYears.find(y => y.id === p.transferred_from_year)?.label || 'سنة سابقة'}`}
                                  color="default"
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                          </TableCell>
                        </TBodyRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            لا توجد دفعات مسجّلة
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            </motion.div>

            {/* Closed years archive */}
            <motion.div {...fadeUp(0.22)}>
              <SectionCard>
                <SectionHeader icon={<HistoryIcon fontSize="small" />} title="أرشيف السنوات المغلقة" color="#7C3AED" />
                <TableContainer sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.07)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TH>السنة</TH>
                        <TH>المدفوع</TH>
                        <TH>الدين المرحّل</TH>
                        <TH>تاريخ الإغلاق</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(student?.payment_history || []).length > 0 ? student.payment_history.map((h, idx) => (
                        <TBodyRow key={h.id || idx} idx={idx}>
                          <TableCell sx={{ fontWeight: 600 }}>{h.year}</TableCell>
                          <TableCell>{parseFloat(h.total_paid || 0).toFixed(2)} ₪</TableCell>
                          <TableCell>
                            {parseFloat(h.carry_forward_amount || 0) > 0
                              ? `${parseFloat(h.carry_forward_amount).toFixed(2)} ₪`
                              : '-'}
                          </TableCell>
                          <TableCell>{h.created_at ? new Date(h.created_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                        </TBodyRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            لا يوجد سجل للطالب
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            </motion.div>
          </>
        )}

        {/* ── Bus ───────────────────────────────────────────────────────── */}
        <motion.div {...fadeUp(0.24)}>
          <SectionCard>
            <SectionHeader icon={<DirectionsBusIcon fontSize="small" />} title="معلومات الباص" color="#0284C7" />
            <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}
              sx={{
                p: 2, borderRadius: 2.5,
                background: student.is_bus_joined ? '#E0F2FE' : '#F8F9FF',
                border: `1px solid ${student.is_bus_joined ? 'rgba(2,132,199,0.18)' : '#E5E7EB'}`,
              }}
            >
              <DirectionsBusIcon sx={{ color: student.is_bus_joined ? '#0284C7' : '#9CA3AF', fontSize: 32 }} />
              {student.is_bus_joined ? (
                <Box>
                  <Typography fontWeight={700} sx={{ color: '#0284C7', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الطالب مسجل في الباص</Typography>
                  {busInfo && (
                    <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      {busInfo.bus_number ? `رقم: ${busInfo.bus_number}` : ''}{busInfo.name ? ` — ${busInfo.name}` : ''}
                    </Typography>
                  )}
                  {busInfo && (busInfo.manager_name || busInfo.driver_name) && (
                    <Typography variant="body2" sx={{ color: '#0284C7', fontFamily: 'Cairo, Tajawal, sans-serif', fontWeight: 600, mt: 0.25 }}>
                      السائق: {busInfo.manager_name || busInfo.driver_name}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography sx={{ color: '#6B7280', fontWeight: 500, fontFamily: 'Cairo, Tajawal, sans-serif' }}>الطالب غير مسجل في الباص</Typography>
              )}
            </Stack>
          </SectionCard>
        </motion.div>

        {/* ── Library ───────────────────────────────────────────────────── */}
        {(() => {
          try {
            const uiPreferences = JSON.parse(localStorage.getItem('uiPreferences') || '{}');
            if (!uiPreferences?.school?.library) return null;
          } catch {
            return null;
          }
          return (
            <motion.div {...fadeUp(0.27)}>
              <SectionCard>
                <SectionHeader icon={<BookIcon fontSize="small" />} title="الكتب المستعارة" color="#5B21F4" />
                {libraryLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress size={32} />
                  </Box>
                ) : libraryTransactions.length > 0 ? (
                  <TableContainer sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.07)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TH>المصدر</TH>
                          <TH>تاريخ الاستعارة</TH>
                          <TH>تاريخ الاستحقاق</TH>
                          <TH>تاريخ الإرجاع</TH>
                          <TH>الحالة</TH>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {libraryTransactions.map((t, idx) => (
                          <TBodyRow key={t.id} idx={idx}>
                            <TableCell>{t.copy_title || t.item_title || '-'}</TableCell>
                            <TableCell>{new Date(t.borrowed_at).toLocaleDateString('en-GB')}</TableCell>
                            <TableCell>
                              <Stack useFlexGap direction="row" alignItems="center" spacing={0.5}>
                                <span>{new Date(t.due_date).toLocaleDateString('en-GB')}</span>
                                {t.status === 'borrowed' && new Date(t.due_date) < new Date() && (
                                  <Chip label="متأخر" color="error" size="small" />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {t.returned_at ? new Date(t.returned_at).toLocaleDateString('en-GB') : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={t.status_display || t.status}
                                color={
                                  t.status === 'borrowed'
                                    ? (new Date(t.due_date) < new Date() ? 'error' : 'primary')
                                    : t.status === 'returned' ? 'success' : 'default'
                                }
                                size="small"
                              />
                            </TableCell>
                          </TBodyRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    لا توجد كتب مستعارة
                  </Typography>
                )}
              </SectionCard>
            </motion.div>
          );
        })()}

        {/* HIDDEN TEMPORARILY - الصحة والبيانات الطبية */}
        {/* <motion.div {...fadeUp(0.30)}>
          <SectionCard noPad>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <StudentHealthSection studentId={studentId} />
            </Box>
          </SectionCard>
        </motion.div> */}

        {/* ── History ───────────────────────────────────────────────────── */}
        <motion.div {...fadeUp(0.33)}>
          <SectionCard>
            <SectionHeader icon={<HistoryIcon fontSize="small" />} title="سجل الطالب في المدرسة" color="#4338CA" />
            <TableContainer sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.07)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TH>السنة</TH>
                    <TH>الحدث</TH>
                    <TH>ملاحظات</TH>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length > 0 ? history.map((record, idx) => (
                    <TBodyRow key={idx} idx={idx}>
                      <TableCell>{record.date}</TableCell>
                      <TableCell>{record.event}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{record.note || '—'}</TableCell>
                    </TBodyRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        لا يوجد سجل للطالب
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </motion.div>
      </Box>

      {/* ── Document Viewer Dialog ─────────────────────────────────────── */}
      <Dialog open={documentViewerOpen} onClose={() => setDocumentViewerOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>عرض الوثيقة</Typography>
          <IconButton onClick={() => setDocumentViewerOpen(false)} size="small" sx={{ color: '#6B7280' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {isImageFile(student.attachment_url || student.attachment) ? (
            <Box sx={{ textAlign: 'center' }}>
              <img src={student.attachment_url || student.attachment} alt="وثيقة الطالب"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }} />
            </Box>
          ) : isPdfFile(student.attachment_url || student.attachment) ? (
            <Box sx={{ height: '70vh' }}>
              <iframe src={student.attachment_url || student.attachment} width="100%" height="100%"
                style={{ border: 'none', borderRadius: 8 }} title="PDF Viewer" />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <AttachFileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>لا يمكن معاينة هذا النوع من الملفات</Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>يمكنك تحميل الملف لعرضه</Typography>
              <Button variant="contained" onClick={handleDownloadDocument}
                sx={{ mt: 2, borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif', bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}
                startIcon={<DownloadIcon />}>
                تحميل الملف
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Close Account Dialog ───────────────────────────────────────── */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: 340 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>تسكير الحساب</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={schoolYears}
            getOptionLabel={(option) => option.label}
            onChange={(e, value) => setYearToClose(value?.label || '')}
            renderInput={(params) => <TextField {...params} label="اختر السنة الدراسية" size="small" sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif', borderColor: '#E5E7EB', color: '#6B7280', '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' } }}>إلغاء</Button>
          <Button variant="contained" onClick={handleConfirmClose} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif', bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>تأكيد</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Payment Dialog ─────────────────────────────────────────── */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pb: 0, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>إضافة دفعة جديدة</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField label="المبلغ" type="number" value={newPayment.amount}
            onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
            fullWidth size="small" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <TextField label="التاريخ" type="date" value={newPayment.date}
            onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
            fullWidth size="small" InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 2, px: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, minWidth: 90, fontFamily: 'Cairo, Tajawal, sans-serif', borderColor: '#E5E7EB', color: '#6B7280', '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' } }}>إلغاء</Button>
          <Button onClick={handleAddPayment} variant="contained" disabled={!newPayment.amount || !newPayment.date}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, minWidth: 90, fontFamily: 'Cairo, Tajawal, sans-serif', bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>
            إضافة
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Shared styles ─────────────────────────────────────────────────────────────

const headerBtnSx = {
  bgcolor: 'rgba(255,255,255,0.18)',
  color: '#fff',
  borderRadius: 2.5,
  fontWeight: 700,
  textTransform: 'none',
  fontFamily: 'Cairo, Tajawal, sans-serif',
  backdropFilter: 'blur(4px)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
  border: '1px solid rgba(255,255,255,0.25)',
  boxShadow: 'none',
};

/** Solid white action button (edit) on the gradient header */
const headerBtnSolidSx = {
  bgcolor: '#fff',
  color: '#4338CA',
  borderRadius: 2.5,
  fontWeight: 700,
  textTransform: 'none',
  fontFamily: 'Cairo, Tajawal, sans-serif',
  '&:hover': { bgcolor: '#EEF2FF' },
  boxShadow: 'none',
};

/** Wrapper card for each section */
const SectionCard = ({ children, noPad }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 3,
      borderRadius: 3,
      border: '1px solid #E5E7EB',
      p: noPad ? 0 : { xs: 2, sm: 3 },
      background: '#fff',
      boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
      overflow: 'hidden',
    }}
  >
    {children}
  </Paper>
);

export default ViewStudentPage;
